import apiClient from '../utils/axios-config';
import { BlogArticle, ArticleCategory } from '../types/blog';
import { logger } from '../utils/logger';

export interface ListArticlesParams {
  page?: number;
  limit?: number;
  category?: ArticleCategory;
  tag?: string;
  search?: string;
  status?: string;
}

export interface ListArticlesResponse {
  articles: BlogArticle[];
  pagination: {
    total: number;
    page: number;
    totalPages: number;
    hasMore: boolean;
  };
}

class BlogService {
  async listArticles(params: ListArticlesParams = {}): Promise<ListArticlesResponse> {
    try {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.category) searchParams.append('category', params.category);
      if (params.tag) searchParams.append('tag', params.tag);
      if (params.search) searchParams.append('search', params.search);
      if (params.status) searchParams.append('status', params.status);

      const url = `/blog${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      logger.apiError('listing articles', error);
      throw error;
    }
  }

  async getArticleBySlug(slug: string): Promise<BlogArticle> {
    try {
      const response = await apiClient.get(`/blog/${slug}`);
      return response.data.article || response.data;
    } catch (error) {
      logger.apiError('fetching article by slug', error);
      throw error;
    }
  }

  async createArticle(data: {
    title: string;
    body: string;
    category: ArticleCategory;
    tags: string[];
    featuredImageUrl: string;
  }): Promise<BlogArticle> {
    try {
      const response = await apiClient.post('/blog', data);
      return response.data.article || response.data;
    } catch (error) {
      logger.apiError('creating article', error);
      throw error;
    }
  }

  async updateArticle(articleId: string, data: Partial<{
    title: string;
    body: string;
    category: ArticleCategory;
    tags: string[];
    featuredImageUrl: string;
  }>): Promise<BlogArticle> {
    try {
      const response = await apiClient.put(`/blog/${articleId}`, data);
      return response.data.article || response.data;
    } catch (error) {
      logger.apiError('updating article', error);
      throw error;
    }
  }

  async deleteArticle(articleId: string): Promise<void> {
    try {
      await apiClient.delete(`/blog/${articleId}`);
    } catch (error) {
      logger.apiError('deleting article', error);
      throw error;
    }
  }

  async getFeaturedArticles(): Promise<BlogArticle[]> {
    try {
      const response = await this.listArticles({ limit: 3, status: 'published' });
      return response.articles.slice(0, 3);
    } catch (error) {
      logger.apiError('fetching featured articles', error);
      throw error;
    }
  }
}

export default new BlogService();
